/**
 * QuizCsvImportDialog — upload a CSV of questions, review the validation
 * summary (dry run), then confirm to import the valid rows.
 */
import { useRef, useState } from 'react';
import { Upload, FileSpreadsheet, CheckCircle2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../../api/client';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../ui/dialog';

export const QuizCsvImportDialog = ({ quizId, open, onClose, onImported }) => {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(false);

  const reset = () => { setFile(null); setSummary(null); };

  const runValidate = async (f) => {
    setBusy(true);
    setSummary(null);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const res = await api.post(`/quizzes/${quizId}/import-csv?dry_run=true`, fd);
      setSummary(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Validation failed');
      setFile(null);
    } finally { setBusy(false); }
  };

  const onPick = (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    setFile(f);
    runValidate(f);
  };

  const onConfirm = async () => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post(`/quizzes/${quizId}/import-csv?dry_run=false`, fd);
      toast.success(`Imported ${res.data.imported} question${res.data.imported === 1 ? '' : 's'}`);
      reset();
      onImported();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Import failed');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { reset(); onClose(); } }}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="quiz-csv-import-dialog">
        <DialogHeader>
          <DialogTitle>Import questions from CSV</DialogTitle>
          <DialogDescription>
            Columns: question_text, question_type (mcq / true_false), option_a…option_f,
            correct_option (letter, or TRUE/FALSE), points, explanation, status (approved / pending).
            Export an existing quiz to get a ready-made template.
          </DialogDescription>
        </DialogHeader>

        <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden"
               onChange={onPick} data-testid="quiz-csv-file-input" />

        {!summary && (
          <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={busy}
                  data-testid="quiz-csv-pick-btn">
            <Upload className="w-4 h-4 mr-2" />
            {busy ? 'Validating…' : 'Choose CSV file'}
          </Button>
        )}

        {summary && (
          <div className="space-y-3" data-testid="quiz-csv-summary">
            <div className="flex items-center gap-2 text-sm">
              <FileSpreadsheet className="w-4 h-4" />
              <span className="font-medium">{file?.name}</span>
              <Badge variant="outline">{summary.total_rows} rows</Badge>
            </div>
            <div className="flex items-center gap-4 text-sm">
              <span className="inline-flex items-center gap-1.5 text-emerald-700">
                <CheckCircle2 className="w-4 h-4" /> {summary.valid} valid
              </span>
              <span className={`inline-flex items-center gap-1.5 ${summary.errors.length ? 'text-red-700' : 'text-muted-foreground'}`}>
                <AlertTriangle className="w-4 h-4" /> {summary.errors.length} with errors
              </span>
            </div>
            {summary.errors.length > 0 && (
              <div className="border rounded-md divide-y max-h-52 overflow-y-auto text-sm" data-testid="quiz-csv-errors">
                {summary.errors.map((er, i) => (
                  <div key={i} className="px-3 py-2">
                    <span className="font-medium">Row {er.row}</span>
                    {er.question ? <span className="text-muted-foreground"> — “{er.question}”</span> : null}
                    <p className="text-xs text-red-700 mt-0.5">{er.message}</p>
                  </div>
                ))}
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              {summary.valid > 0
                ? `Only the ${summary.valid} valid row${summary.valid === 1 ? '' : 's'} will be imported${summary.errors.length ? ' — rows with errors are skipped' : ''}.`
                : 'Nothing importable — fix the errors above and re-upload.'}
            </p>
          </div>
        )}

        <DialogFooter>
          {summary && (
            <Button variant="outline" onClick={reset} disabled={busy} data-testid="quiz-csv-reupload-btn">
              Choose another file
            </Button>
          )}
          <Button onClick={onConfirm} disabled={busy || !summary || summary.valid === 0}
                  data-testid="quiz-csv-confirm-btn">
            {busy ? 'Importing…' : `Import ${summary?.valid ?? 0} question${(summary?.valid ?? 0) === 1 ? '' : 's'}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
